import React, { useState, useEffect } from 'react';
import forwardIcon from '../assets/forward.png';
import uploadIcon from '../assets/upload.png';
import { Table, Column } from './Table';
import { saiFilesApi, SAIFileResponse } from '../services/api';
import { formatRelativeTime } from '../utils/formatters';

export interface SAIFile {
  id: string;
  fileName: string;
  datapoints: number;
  models: number;
  size: number;
  uploaded: string;
}

interface AIModelSelectionProps {
  onBack: () => void;
  onNext: (selectedFile: SAIFileResponse) => void;
}

// Transform backend response to frontend format
const transformSAIFile = (file: SAIFileResponse): SAIFile => ({
  id: file.id.toString(),
  fileName: file.filename,
  datapoints: file.datapoint_count,
  models: file.model_count,
  size: parseFloat((file.file_size / (1024 * 1024)).toFixed(2)),
  uploaded: formatRelativeTime(file.uploaded_at),
});

export const AIModelSelection: React.FC<AIModelSelectionProps> = ({ onBack, onNext }) => {
  const [selectedOption, setSelectedOption] = useState<'existing' | 'upload'>('existing');
  const [selectedFile, setSelectedFile] = useState<SAIFile | null>(null);
  const [saiFiles, setSaiFiles] = useState<SAIFile[]>([]);
  const [, setSaiFileResponses] = useState<SAIFileResponse[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [uploadedFile, setUploadedFile] = useState<{ name: string; size: number } | null>(null);
  const [selectedFileToUpload, setSelectedFileToUpload] = useState<File | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Fetch SAI files from backend
  useEffect(() => {
    const fetchSAIFiles = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await saiFilesApi.list();
        const transformedFiles = (response || []).map(transformSAIFile);
        setSaiFiles(transformedFiles);
        setSaiFileResponses(response || []);
        setTotalPages(1);
      } catch (err) {
        console.error('Error fetching SAI files:', err);
        setError('Failed to load SAI files. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (selectedOption === 'existing') {
      fetchSAIFiles();
    }
  }, [currentPage, selectedOption]);

  const tableColumns: Column[] = [
    { key: 'fileName', header: 'File Name' },
    { key: 'datapoints', header: 'No. of Datapoints' },
    { key: 'models', header: 'No. of Models' },
    { key: 'size', header: 'Size (MB)', render: (item) => `${item.size} MB` },
    { key: 'uploaded', header: 'Uploaded' },
  ];

  const handleFileSelect = async (fileId: string) => {
    const file = saiFiles.find(f => f.id === fileId);
    if (!file) return;

    try {
      const detail = await saiFilesApi.getDetail(parseInt(fileId));
      setSelectedFile(file);
      onNext(detail);
    } catch (err) {
      console.error('Error fetching file detail:', err);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleUpload = () => {
    // Trigger the hidden file input
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.sai')) {
        alert('Please upload a valid .sai file');
        return;
      }

      // Store the selected file (don't upload yet)
      setSelectedFileToUpload(file);
      setError(null);

      // Convert bytes to KB for display
      const sizeInKB = (file.size / 1024).toFixed(1);
      setUploadedFile({ name: file.name, size: parseFloat(sizeInKB) });
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setSelectedFileToUpload(null);
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleContinue = async () => {
    if (!selectedFileToUpload) {
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // Upload file to backend (includes parameters automatically created via signal)
      const response = await saiFilesApi.create(selectedFileToUpload);
      const detail = await saiFilesApi.getDetail(response.id);

      // Proceed to next step with the full file detail
      onNext(detail);

      // Refresh the list if user switches back to existing files
      setCurrentPage(1); // Reset to first page to see the newly uploaded file
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="wizard-step-container">
      <div className="wizard-step-header">
        <button className="back-button" onClick={onBack}>
          <span className="back-arrow">←</span> Back
        </button>
        <h1>Select or Upload SAI file</h1>
      </div>

      <div className="file-source-options">
        <div
          className={`file-source-card ${selectedOption === 'existing' ? 'selected' : ''}`}
          onClick={() => {
            setSelectedOption('existing');
            setError(null);
          }}
        >
          <div className="file-source-content">
            <div className="file-source-icon">
              <img src={forwardIcon} alt="Existing files" />
            </div>
            <div className="file-source-text">
              <h3>Existing</h3>
              <p>Select from your existing .sai - Simerics AI files</p>
            </div>
            {selectedOption === 'existing' && (
              <div className="checkmark-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                  <rect width="20.48" height="20.48" rx="10.24" fill="#29B829"/>
                  <path d="M6.40039 10.24L8.96039 12.8L14.0804 7.67999" stroke="white" stroke-width="2.33333" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            )}
          </div>
        </div>

        <div className="file-source-divider">or</div>

        <div
          className={`file-source-card ${selectedOption === 'upload' ? 'selected' : ''}`}
          onClick={() => {
            setSelectedOption('upload');
            setError(null);
          }}
        >
          <div className="file-source-content">
            <div className="file-source-icon">
              <img src={uploadIcon} alt="Upload new" />
            </div>
            <div className="file-source-text">
              <h3>Upload New</h3>
              <p>Upload new .sai - Simerics AI file</p>
            </div>
            {selectedOption === 'upload' && (
              <div className="checkmark-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                  <rect width="20.48" height="20.48" rx="10.24" fill="#29B829"/>
                  <path d="M6.40039 10.24L8.96039 12.8L14.0804 7.67999" stroke="white" stroke-width="2.33333" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedOption === 'existing' && (
        <>
          {loading && (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>Loading SAI files...</p>
            </div>
          )}
          {error && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#dc3545' }}>
              <p>{error}</p>
              <button
                className="btn btn-primary"
                onClick={() => setCurrentPage(currentPage)}
                style={{ marginTop: '1rem' }}
              >
                Retry
              </button>
            </div>
          )}
          {!loading && !error && (
            <Table
              items={saiFiles}
              columns={tableColumns}
              selectedItemId={selectedFile?.id || null}
              onItemSelect={handleFileSelect}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {selectedOption === 'upload' && (
        <div className="upload-area">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".sai"
            style={{ display: 'none' }}
          />
          {!uploadedFile ? (
            <div className="upload-dropzone">
              <div className="upload-icon-circle">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 20 20" fill="none">
                  <path d="M6.6665 13.3333L9.99984 10M9.99984 10L13.3332 13.3333M9.99984 10V17.5M16.6665 13.9524C17.6844 13.1117 18.3332 11.8399 18.3332 10.4167C18.3332 7.88536 16.2811 5.83333 13.7498 5.83333C13.5677 5.83333 13.3974 5.73833 13.3049 5.58145C12.2182 3.73736 10.2119 2.5 7.9165 2.5C4.46472 2.5 1.6665 5.29822 1.6665 8.75C1.6665 10.4718 2.36271 12.0309 3.48896 13.1613" stroke="#103097" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h3 className="upload-title">Upload File</h3>
              <p className="upload-subtitle">Supported formats: Simerics AI file (.sai)</p>
              {error && selectedOption === 'upload' && (
                <p style={{ color: '#dc3545', marginBottom: '1rem' }}>{error}</p>
              )}
              <button
                className="btn btn-primary upload-btn"
                onClick={handleUpload}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M17.5 12.5V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V12.5M14.1667 6.66667L10 2.5M10 2.5L5.83333 6.66667M10 2.5V12.5" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Select File
              </button>
            </div>
          ) : (
            <div className="upload-success">
              <div className="success-icon-circle">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h3 className="success-title">File selected</h3>
              <p className="success-filename">{uploadedFile.name} ({uploadedFile.size} KB)</p>
              {error && selectedOption === 'upload' && (
                <p style={{ color: '#dc3545', marginBottom: '1rem' }}>{error}</p>
              )}
              <button className="btn-remove" onClick={handleRemoveFile}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M2.5 5H4.16667H17.5M6.66667 5V3.33333C6.66667 2.89131 6.84226 2.46738 7.15482 2.15482C7.46738 1.84226 7.89131 1.66667 8.33333 1.66667H11.6667C12.1087 1.66667 12.5326 1.84226 12.8452 2.15482C13.1577 2.46738 13.3333 2.89131 13.3333 3.33333V5M15.8333 5V16.6667C15.8333 17.1087 15.6577 17.5326 15.3452 17.8452C15.0326 18.1577 14.6087 18.3333 14.1667 18.3333H5.83333C5.39131 18.3333 4.96738 18.1577 4.65482 17.8452C4.34226 17.5326 4.16667 17.1087 4.16667 16.6667V5H15.8333Z" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Remove file
              </button>
            </div>
          )}

          <button
            className="btn btn-primary continue-btn-full"
            disabled={!selectedFileToUpload || uploading}
            onClick={handleContinue}
          >
            {uploading ? (
              <>Uploading...</>
            ) : (
              <>
                Continue <span className="arrow-right">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4.16699 10H15.8337M15.8337 10L10.0003 4.16669M15.8337 10L10.0003 15.8334" stroke="#273444" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M4.16699 10H15.8337M15.8337 10L10.0003 4.16669M15.8337 10L10.0003 15.8334" stroke="black" stroke-opacity="0.2" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M4.16699 10H15.8337M15.8337 10L10.0003 4.16669M15.8337 10L10.0003 15.8334" stroke="black" stroke-opacity="0.2" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

